import React, { lazy, Suspense, useMemo } from 'react';
import { ErrorBoundary, ErrorPage, loadComponent, PageLoader } from '@cko/dashboard-shared';

import { ViewInstance } from '../../config/shared/types/page';
import {
  LoggerProvider,
  useMfeLogger,
  useNavigation,
  useUserContextValidation,
} from '../../providers';
import { LAYOUT_ERRORS } from '../errors';

type Props = {
  viewInstance: ViewInstance;
  viewProps?: Record<string, unknown>;
};

export const ViewRenderer = ({ viewInstance, viewProps }: Props) => {
  const { validate } = useUserContextValidation();
  const { getViewById } = useNavigation();

  const viewDefinition = useMemo(() => getViewById(viewInstance.view_id), [getViewById]);

  const mfeLogger = useMfeLogger(
    viewDefinition?.mfe,
    viewDefinition?.module,
    viewDefinition?.metadata.team,
  );

  if (!viewDefinition) {
    // Need to fire a Datadog RUM error here, or another way to notify devs that a view is missing.
    return <ErrorPage />;
  }

  const isViewAccessible = validate(
    {
      feature_flags: viewDefinition.feature_flags,
      permissions: viewDefinition.permissions,
    },
    'VIEW',
  );
  if (!isViewAccessible) {
    return null;
  }

  // This uses the old method of loading MFE components. We will likely change this approach in the
  // future to loading on demand or loading all components up front.
  const ViewComponent = lazy(
    loadComponent(
      viewDefinition.mfe,
      viewDefinition.module,
      () => Promise.resolve({ default: ErrorPage }),
      mfeLogger,
    ),
  );

  return (
    <LoggerProvider logger={mfeLogger}>
      <ErrorBoundary errorName={LAYOUT_ERRORS.viewRenderer}>
        <Suspense fallback={<PageLoader />}>
          <ViewComponent {...viewProps} />
        </Suspense>
      </ErrorBoundary>
    </LoggerProvider>
  );
};
